import * as React from 'react';
import { GET_LAWYER_CASE_DETAILS, GET_LAWYER_CASE_METRICS } from '@/api';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { casesLawyerService } from '@/api/casesLawyer';
import { CaseContext } from '@/context/CaseContext';
import { PhaseName, StatusTypes } from '@/interfaces';
import { useEventSource } from '@/hooks/useEventSource';
import { usePermissions } from '@/context/PermissionsContext';
import { useHandleError } from '@/hooks/useHandleError';
import { useCaseClientActions, useSendDocumentModals } from '@/hooks';
import { CaseDetailsLayout } from './case-details-layout';
import { CaseAdministration } from './case-administration';
import { ActionRequired } from './action-required';
import { ClientDocuments } from './documentation';
import { CaseMetrics } from './metrics';
import { getValidCaseId } from '@/utils';

interface Props {
	screen?: string;
}

export const LawyerCasesDetailsScreen: React.FC<Props> = ({ screen }) => {
	const router = useRouter();
	const { getErrorsByCase } = useHandleError();

	const caseId = getValidCaseId(router.query.id);
	const { allowed } = usePermissions();

	const { data: stepNotification } = useEventSource({
		url: `${process.env.NEXT_PUBLIC_API}/sse/action-resolved/subscribe`,
	});

	const firstParsed = stepNotification ? JSON.parse(stepNotification) : null;
	const parsedNotification = firstParsed ? JSON.parse(firstParsed.data) : null;

	const notCaseId = parsedNotification?.caseId;
	const notDate = parsedNotification?.date;
	const hasPermission = allowed.has('case_admin');

	const readOnlyCases: StatusTypes[] = [
		'Aborted',
		'Cancelled',
		'Case Aborted',
		'Case Resolved',
		'Resolved Case',
		'Case Unsuccessful',
	];

	const { data: caseLawyerDetails, refetch: refetchCaseLawyerDetails } =
		useQuery({
			queryKey: [GET_LAWYER_CASE_DETAILS, caseId],
			queryFn: () => casesLawyerService.getCaseDetails(caseId as string),
			enabled: !!caseId,
		});

	const { data: caseMetrics } = useQuery({
		queryKey: [GET_LAWYER_CASE_METRICS, caseId],
		queryFn: () => casesLawyerService.getCaseMetrics(caseId as string),
		enabled: !!caseId,
	});

	React.useEffect(() => {
		//this is to fix the problem where if you came from a different page the scroll stay at the bottom
		const timeout = setTimeout(() => {
			document
				.getElementById('global-container')
				?.scrollTo({ top: 0, behavior: 'smooth' });
		}, 100);

		return () => clearTimeout(timeout);
	}, []);

	React.useEffect(() => {
		if (caseId && notCaseId === caseId) {
			refetchCaseLawyerDetails();
		}
	}, [notDate, notCaseId, caseId]);

	const isCaseReadOnly = caseLawyerDetails?.data.progress_name
		? readOnlyCases.includes(caseLawyerDetails?.data.progress_name) ||
		  !hasPermission
		: false;

	const casesErrors = getErrorsByCase(caseId as string);

	const [saveStatus, setSaveStatus] = React.useState({
		isSaving: false,
		isError: false,
	});

	const { selectedUserIds, setSelectedUserIds } = useCaseClientActions();

	const { renderSendDocumentModals } = useSendDocumentModals({
		caseId: caseId || '',
		caseName: caseLawyerDetails?.data.title || '',
		phaseName: (caseLawyerDetails?.data.phase_name as PhaseName) || '',
		isToAllUsers: true,
		selectedUserIds,
		setSelectedUserIds,
	});

	return (
		<CaseContext.Provider
			value={{
				caseDetails: caseLawyerDetails?.data,
				isCaseReadOnly,
				saveStatus,
				setSaveStatus: () => setSaveStatus({ isSaving: false, isError: false }),
			}}
		>
			<CaseDetailsLayout
				namePhase={!screen ? 'Investigation' : undefined}
				numberPhase={!screen ? 1 : undefined}
			>
				{!screen && caseId && (
					<CaseAdministration
						caseId={caseId}
						caseLawyerDetails={caseLawyerDetails?.data}
						casesErrors={casesErrors}
					/>
				)}
				{screen && (
					<div className="flex flex-col gap-6 w-full">
						<div className="w-full min-h-[100px] overflow-x-auto overflow-y-hidden">
							{screen === 'actions-required' && (
								<ActionRequired notCaseId={notCaseId} notDate={notDate} />
							)}
							{screen === 'metrics' && (
								<CaseMetrics
									caseMetrics={caseMetrics?.data}
									caseStatus={caseLawyerDetails?.data.status?.name}
									caseLawyerDetails={caseLawyerDetails?.data}
								/>
							)}
							{screen === 'client-documents' && caseId && (
								<ClientDocuments
									id={caseId}
									caseName={caseLawyerDetails?.data.title ?? ''}
								/>
							)}
						</div>
					</div>
				)}
			</CaseDetailsLayout>

			{renderSendDocumentModals}
		</CaseContext.Provider>
	);
};
